import { format } from "date-fns";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { MouseEvent, ReactNode, useRef, useState } from "react";

import { cn } from "@/app/utils/cn";
import { formatName } from "@/app/utils/formatName";
import masks from "@/app/utils/masks";
import { StatusEnum } from "@/app/utils/status";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { PartnerType } from "@/app/utils/partners.enum";
import { SupportCategoryEnum } from "@/app/utils/supportCategoryEnum";
import { PartnerImage } from "@/view/components/PartnerImage";
import { BasicAvatar } from "@/view/components/BasicAvatar";
import Status from "@/view/components/Status";
import { Badge } from "@/view/components/ui/badge";
import {
  SUPPORT_REASON_LABEL,
  SUPPORT_STATUS_BADGE_CLASSNAME,
  SUPPORT_STATUS_LABEL,
  SupportRequest,
  SupportRequestReason,
} from "@/app/entities/SupportRequest";

import {
  SEARCH_CATEGORIES,
  SUPPORT_REQUESTS_CATEGORY,
  SearchCategory,
  SearchCategoryState,
  SupportSearchResults as Results,
} from "../useSupportController";
import { formatDaysAgo } from "../formatDaysAgo";
import { resolveRequester } from "../resolveRequester";
import { useLoadMoreOnScroll } from "./useLoadMoreOnScroll";
import { usePersonTransactions } from "./usePersonTransactions";

interface SupportSearchResultsProps {
  query: string;
  results: Results;
  pending: SearchCategoryState;
  errors: SearchCategoryState;
  onOpenCustomer: (id: string) => void;
  onOpenSeller: (id: string) => void;
  onOpenTransaction: (id: string) => void;
  onOpenTeam: (id: string) => void;
  onOpenSupportRequest: (request: SupportRequest) => void;
  productsCatalog?: ProductCatalogItem[];
  searchPages: Record<
    "people" | "transactions" | "supportRequests",
    SearchPageState
  >;
  searchTotals: SearchCategoryState<number>;
}

export interface SearchPageState {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

type Person = Results["customers"][number];
type Transaction = Results["transactions"][number];
type Team = Results["teams"][number];

const TAB_LABEL: Record<SearchCategory, string> = {
  [SupportCategoryEnum.CUSTOMER]: "Clientes",
  [SupportCategoryEnum.USER]: "Vendedores",
  [SupportCategoryEnum.TRANSACTION]: "Propostas",
  [SupportCategoryEnum.TEAMS]: "Equipes",
  [SUPPORT_REQUESTS_CATEGORY]: "Chamados",
};

const EMPTY_LABEL: Record<SearchCategory, string> = {
  [SupportCategoryEnum.CUSTOMER]: "Nenhum cliente encontrado",
  [SupportCategoryEnum.USER]: "Nenhum vendedor encontrado",
  [SupportCategoryEnum.TRANSACTION]: "Nenhuma proposta encontrada",
  [SupportCategoryEnum.TEAMS]: "Nenhuma equipe encontrada",
  [SUPPORT_REQUESTS_CATEGORY]: "Nenhum chamado encontrado",
};

// busca por contrato acha proposta direto; por CPF ou nome, não — aí as
// propostas mostradas são as do vendedor encontrado
const sellerProposalsOf = (results: Results) =>
  results.transactions.length
    ? 0
    : (results.sellers[0]?.transactionsCount ?? 0);

// quantos itens já vieram — é o que a aba tem para mostrar agora
const loadedOf = (category: SearchCategory, results: Results) => {
  if (category === SupportCategoryEnum.CUSTOMER)
    return results.customers.length;
  if (category === SupportCategoryEnum.USER) return results.sellers.length;
  if (category === SupportCategoryEnum.TEAMS) return results.teams.length;
  if (category === SUPPORT_REQUESTS_CATEGORY)
    return results.supportRequests.length;
  return results.transactions.length || sellerProposalsOf(results);
};

// o badge mostra o total da busca, não só o que já foi carregado
const countOf = (
  category: SearchCategory,
  results: Results,
  totals: SearchCategoryState<number>,
) => totals[category] || loadedOf(category, results);

interface ResultCardProps {
  title: string;
  onOpen: () => void;
  className?: string;
  children: ReactNode;
}

// card inteiro abre o item (em outra aba); a seta só sinaliza, no canto
const ResultCard = ({
  title,
  onOpen,
  className,
  children,
}: ResultCardProps) => (
  <div
    role="link"
    tabIndex={0}
    title={title}
    onClick={onOpen}
    onKeyDown={(event) => {
      if (event.key === "Enter") onOpen();
    }}
    className={cn(
      "relative cursor-pointer rounded-xl border border-gray01 bg-white p-4 py-3 pr-10 transition-colors hover:border-black04 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
      className,
    )}
  >
    <ArrowUpRight className="absolute right-3 top-3 h-4 w-4 text-black04" />
    {children}
  </div>
);

// o que é clicável dentro do card não pode abrir o card junto
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

const formatCpf = (cpf?: string) => (cpf ? masks.cpf(cpf) : "-");
const formatPhone = (phone?: string) => (phone ? masks.phone(phone) : "-");

export const SupportSearchResults = ({
  query,
  results,
  pending,
  errors,
  onOpenCustomer,
  onOpenSeller,
  onOpenTransaction,
  onOpenTeam,
  onOpenSupportRequest,
  productsCatalog,
  searchPages,
  searchTotals,
}: SupportSearchResultsProps) => {
  // toda busca abre nas Propostas, mesmo com Chamados primeiro na lista
  const [selectedTab, setSelectedTab] = useState<SearchCategory>(
    SupportCategoryEnum.TRANSACTION,
  );

  const [seller] = results.sellers;
  const showsSellerProposals = !!sellerProposalsOf(results);

  const sellerProposals = usePersonTransactions({
    personId: seller?._id ?? "",
    role: "seller",
    filter:
      showsSellerProposals && selectedTab === SupportCategoryEnum.TRANSACTION
        ? "all"
        : null,
  });

  const activeCount = loadedOf(selectedTab, results);

  const activePages =
    selectedTab === SupportCategoryEnum.TRANSACTION
      ? showsSellerProposals
        ? null
        : searchPages.transactions
      : selectedTab === SUPPORT_REQUESTS_CATEGORY
        ? searchPages.supportRequests
        : searchPages.people;

  const sentinelRef = useRef<HTMLDivElement>(null);

  useLoadMoreOnScroll({
    anchorRef: sentinelRef,
    hasNextPage: activePages?.hasNextPage,
    isFetchingNextPage: activePages?.isFetchingNextPage,
    fetchNextPage: () => activePages?.fetchNextPage(),
  });

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        Resultados para{" "}
        <span className="normal-case text-black01">{query}</span>
      </p>

      <div role="tablist" className="flex flex-wrap gap-2">
        {SEARCH_CATEGORIES.map((category) => {
          const isActive = category === selectedTab;
          const isLoading = pending[category];
          const count = countOf(category, results, searchTotals);

          return (
            <button
              key={category}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setSelectedTab(category)}
              className={cn(
                "flex items-center gap-2 rounded-lg border border-gray01 bg-white px-3 py-1.5 text-sm font-medium text-black03 transition-colors hover:bg-gray-50",
                !isLoading && count == 0 && "border-dashed text-black04",
                isActive &&
                  "border-solid border-primary bg-primary text-white hover:bg-primary",
              )}
            >
              {TAB_LABEL[category]}
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span
                  className={cn(
                    "rounded-md bg-gray02 px-1.5 text-xs font-semibold text-black03",
                    isActive && "bg-white/20 text-white",
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" className="flex flex-col gap-4">
        {activeCount > 0 ? (
          <div className="flex flex-col gap-4">
            {selectedTab === SupportCategoryEnum.CUSTOMER &&
              results.customers.map((customer) => (
                <PersonItem
                  key={customer._id}
                  person={customer}
                  actionLabel="Abrir ficha do cliente"
                  sinceLabel="cliente desde"
                  variant="customer"
                  onOpen={() => onOpenCustomer(customer._id)}
                  onOpenTransaction={onOpenTransaction}
                  onOpenTeam={onOpenTeam}
                  productsCatalog={productsCatalog}
                />
              ))}

            {selectedTab === SupportCategoryEnum.USER &&
              results.sellers.map((seller) => (
                <PersonItem
                  key={seller._id}
                  person={seller}
                  actionLabel="Abrir ficha do vendedor"
                  sinceLabel="vendedor desde"
                  variant="seller"
                  onOpen={() => onOpenSeller(seller._id)}
                  onOpenTransaction={onOpenTransaction}
                  onOpenTeam={onOpenTeam}
                  productsCatalog={productsCatalog}
                />
              ))}

            {selectedTab === SupportCategoryEnum.TRANSACTION &&
              (showsSellerProposals ? (
                <SellerProposals
                  proposals={sellerProposals}
                  onOpenTransaction={onOpenTransaction}
                  productsCatalog={productsCatalog}
                />
              ) : (
                results.transactions.map((transaction) => (
                  <TransactionItem
                    key={transaction._id}
                    transaction={transaction}
                    onOpen={() => onOpenTransaction(transaction._id)}
                    productsCatalog={productsCatalog}
                  />
                ))
              ))}

            {selectedTab === SupportCategoryEnum.TEAMS &&
              results.teams.map((team) => (
                <TeamItem
                  key={team._id}
                  team={team}
                  onOpen={() => onOpenTeam(team._id)}
                />
              ))}

            {selectedTab === SUPPORT_REQUESTS_CATEGORY &&
              results.supportRequests.map((request) => (
                <SupportRequestItem
                  key={request._id}
                  request={request}
                  onOpen={() => onOpenSupportRequest(request)}
                />
              ))}

            {!!activePages?.hasNextPage && (
              <div ref={sentinelRef} aria-hidden className="h-1" />
            )}

            {!!activePages?.isFetchingNextPage && (
              <div className="flex justify-center py-3">
                <Loader2 className="h-4 w-4 animate-spin text-black04" />
              </div>
            )}
          </div>
        ) : (
          <p className="flex items-center justify-center gap-2 py-10 text-sm text-black04">
            {pending[selectedTab] && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {pending[selectedTab]
              ? "Buscando..."
              : errors[selectedTab]
                ? "Não foi possível buscar, tente novamente"
                : EMPTY_LABEL[selectedTab]}
          </p>
        )}
      </div>
    </div>
  );
};

interface SupportRequestItemProps {
  request: SupportRequest;
  onOpen: () => void;
}

const SupportRequestItem = ({ request, onOpen }: SupportRequestItemProps) => {
  const requester = resolveRequester(request);
  const isWhatsappDirect =
    request.reason === SupportRequestReason.WHATSAPP_DIRECT;

  return (
    <ResultCard title="Abrir chamado" onOpen={onOpen}>
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-lg font-semibold text-black01">
            #{request.number || "-"}
          </span>
          <Badge
            className={cn(
              "whitespace-nowrap border-transparent",
              SUPPORT_STATUS_BADGE_CLASSNAME[request.status],
            )}
          >
            {SUPPORT_STATUS_LABEL[request.status]}
          </Badge>
        </div>
        <span className="text-xs text-black04">
          {[
            requester.name ? formatName(requester.name) : "Não identificado",
            requester.cpf && `CPF ${formatCpf(requester.cpf)}`,
            !isWhatsappDirect && SUPPORT_REASON_LABEL[request.reason],
            request.updatedAt &&
              `atualizado ${formatDaysAgo(request.updatedAt)}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
    </ResultCard>
  );
};

interface TeamItemProps {
  team: Team;
  onOpen: () => void;
}

const TeamItem = ({ team, onOpen }: TeamItemProps) => (
  <ResultCard title="Abrir equipe" onOpen={onOpen}>
    <div className="flex min-w-0 flex-col gap-1">
      <span className="truncate text-lg font-semibold text-black01">
        {team.name}
      </span>
      <span className="text-xs text-black04">
        {[
          team.code && `código ${team.code}`,
          team.leaderName && `líder ${formatName(team.leaderName)}`,
          `${team.membersCount ?? 0} ${
            team.membersCount === 1 ? "vendedor" : "vendedores"
          }`,
        ]
          .filter(Boolean)
          .join(" · ")}
      </span>
    </div>
  </ResultCard>
);

interface SellerProposalsProps {
  proposals: ReturnType<typeof usePersonTransactions>;
  onOpenTransaction: (transactionId: string) => void;
  productsCatalog?: ProductCatalogItem[];
}

// as propostas do vendedor ocupam a aba inteira, com rolagem e paginação próprias
const SellerProposals = ({
  proposals,
  onOpenTransaction,
  productsCatalog,
}: SellerProposalsProps) => {
  const {
    items,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    sentinelRef,
  } = proposals;

  if (isLoading) {
    return (
      <p className="flex items-center justify-center gap-2 py-10 text-sm text-black04">
        <Loader2 className="h-4 w-4 animate-spin" />
        Buscando...
      </p>
    );
  }

  return (
    <div className="flex flex-col rounded-lg border border-gray01">
      <div className="flex flex-col divide-y divide-gray01">
        {items.map((item) => (
          <button
            key={item._id}
            type="button"
            onClick={() => onOpenTransaction(item._id)}
            className="flex flex-col gap-3 p-3 text-left transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
          >
            <span className="flex min-w-0 flex-1 items-center gap-4">
              {!!item.type && (
                <PartnerImage
                  type={item.type as PartnerType}
                  products={productsCatalog}
                />
              )}

              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-black04">
                  Contrato
                </span>
                <span className="truncate text-sm font-semibold text-black01">
                  {item.contract || "-"}
                </span>
              </span>

              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-black04">
                  Produto
                </span>
                <span className="truncate text-sm font-medium uppercase text-black03">
                  {item.type ? item.type.replace(/_/g, " ") : "-"}
                </span>
              </span>
            </span>

            <span className="flex items-center gap-3">
              <Status
                status={item.status as StatusEnum}
                className="w-fit whitespace-nowrap px-2"
              />

              <span className="whitespace-nowrap text-xs text-black04">
                Atualizado {item.updatedAt && formatDaysAgo(item.updatedAt)}
              </span>
            </span>
          </button>
        ))}

        {hasNextPage && <div ref={sentinelRef} aria-hidden className="h-1" />}

        {isFetchingNextPage && (
          <div className="flex justify-center py-3">
            <Loader2 className="h-4 w-4 animate-spin text-black04" />
          </div>
        )}
      </div>

      {items.length < total && (
        <p className="border-t border-gray01 p-3 text-xs text-black04">
          Mostrando {items.length} de {total}
        </p>
      )}
    </div>
  );
};

type OpenCard = "documents" | "proposals" | "teams" | null;

interface PersonItemProps {
  person: Person;
  actionLabel: string;
  sinceLabel: string;
  variant: "customer" | "seller";
  onOpen: () => void;
  onOpenTransaction: (transactionId: string) => void;
  onOpenTeam: (teamId: string) => void;
  productsCatalog?: ProductCatalogItem[];
}

const PersonItem = ({
  person,
  actionLabel,
  sinceLabel,
  variant,
  onOpen,
  onOpenTransaction,
  onOpenTeam,
  productsCatalog,
}: PersonItemProps) => {
  const [openCard, setOpenCard] = useState<OpenCard>(null);

  const teams = person.teams ?? [];
  const [team] = teams;

  const toggleCard = (card: OpenCard) =>
    setOpenCard((current) => (current === card ? null : card));

  // a lista só é buscada quando o card é aberto
  const {
    items: openItems,
    total: openTotal,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    sentinelRef,
  } = usePersonTransactions({
    personId: person._id,
    role: variant,
    filter:
      openCard === "documents"
        ? "documents"
        : openCard === "proposals"
          ? "pending"
          : null,
  });

  const isTeamsOpen = openCard === "teams";

  return (
    <ResultCard
      title={actionLabel}
      onOpen={onOpen}
      className="flex flex-col gap-3"
    >
      <div className="flex min-w-0 items-center gap-3">
        <BasicAvatar
          name={person.name}
          profileImageUrl={person.profileImageUrl}
          style={{ backgroundColor: "#edeef0" }}
        />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-lg font-semibold text-black01">
            {formatName(person.name)}
          </span>
          <span className="text-xs text-black04">
            CPF {formatCpf(person.cpf)} · {formatPhone(person.phone)}
            {person.createdAt &&
              ` · ${sinceLabel} ${format(new Date(person.createdAt), "MM/yyyy")}`}
          </span>
        </div>
      </div>

      {/* documentos, propostas e equipe são do vendedor; a ficha do cliente é só o cabeçalho */}
      {variant === "seller" && (
        <div
          onClick={stopPropagation}
          className="grid cursor-default gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          <RelatedCard
            title="Equipe"
            count={person.teamsCount ?? 0}
            main={team?.name ?? "Sem equipe"}
            subtitle={
              team
                ? [
                    team.leaderName && `líder ${team.leaderName}`,
                    `${team.membersCount ?? 0} vendedores`,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : "vendedor sem equipe"
            }
            isOpen={isTeamsOpen}
            onClick={teams.length ? () => toggleCard("teams") : undefined}
          />

          <RelatedCard
            title="Documentos"
            count={person.documentsTransactionsCount ?? 0}
            main={`${person.documentsCount ?? 0} ${
              person.documentsCount === 1 ? "pendente" : "pendentes"
            }`}
            isOpen={openCard === "documents"}
            isLoading={openCard === "documents" && isLoading}
            onClick={() => toggleCard("documents")}
          />

          <RelatedCard
            title="Propostas"
            count={person.transactionsCount ?? 0}
            main={`${person.pendingTransactionsCount ?? 0} ${
              person.pendingTransactionsCount === 1 ? "pendente" : "pendentes"
            }`}
            isOpen={openCard === "proposals"}
            isLoading={openCard === "proposals" && isLoading}
            onClick={() => toggleCard("proposals")}
          />
        </div>
      )}

      {isTeamsOpen && (
        <div
          onClick={stopPropagation}
          className="flex cursor-default flex-col rounded-lg border border-gray01"
        >
          <div className="flex flex-col divide-y divide-gray01">
            {teams.map((item) => (
              <button
                key={item._id}
                type="button"
                onClick={() => onOpenTeam(item._id)}
                className="flex flex-col gap-1 p-3 text-left transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="min-w-0 truncate text-sm font-semibold text-black01">
                  {item.name}
                </span>

                <span className="whitespace-nowrap text-xs text-black04">
                  {[
                    item.leaderName && `líder ${formatName(item.leaderName)}`,
                    `${item.membersCount ?? 0} ${
                      item.membersCount === 1 ? "vendedor" : "vendedores"
                    }`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
            ))}
          </div>

          {teams.length < (person.teamsCount ?? 0) && (
            <p className="border-t border-gray01 p-3 text-xs text-black04">
              Mostrando {teams.length} de {person.teamsCount}
            </p>
          )}
        </div>
      )}

      {openItems.length > 0 && (
        <div
          onClick={stopPropagation}
          className="flex cursor-default flex-col rounded-lg border border-gray01"
        >
          <div className="flex flex-col divide-y divide-gray01">
            {openItems.map((item) => (
              <button
                key={item._id}
                type="button"
                onClick={() => onOpenTransaction(item._id)}
                className="flex flex-col gap-3 p-3 text-left transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="flex min-w-0 flex-1 items-center gap-4">
                  {!!item.type && (
                    <PartnerImage
                      type={item.type as PartnerType}
                      products={productsCatalog}
                    />
                  )}

                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-black04">
                      Contrato
                    </span>
                    <span className="truncate text-sm font-semibold text-black01">
                      {item.contract || "-"}
                    </span>
                  </span>

                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-black04">
                      Produto
                    </span>
                    <span className="truncate text-sm font-medium uppercase text-black03">
                      {item.type ? item.type.replace(/_/g, " ") : "-"}
                    </span>
                  </span>
                </span>

                <span className="flex items-center gap-3">
                  {openCard === "documents" && (
                    <Status
                      status={item.status as StatusEnum}
                      className="w-fit whitespace-nowrap px-2"
                    />
                  )}

                  <span className="whitespace-nowrap text-xs text-black04">
                    {openCard === "documents" &&
                      `${item.documentsCount ?? 0} ${
                        item.documentsCount === 1 ? "documento" : "documentos"
                      }`}
                    {openCard === "documents" && item.updatedAt && " · "}
                    Atualizado {item.updatedAt && formatDaysAgo(item.updatedAt)}
                  </span>
                </span>
              </button>
            ))}

            {hasNextPage && (
              <div ref={sentinelRef} aria-hidden className="h-1" />
            )}

            {isFetchingNextPage && (
              <div className="flex justify-center py-3">
                <Loader2 className="h-4 w-4 animate-spin text-black04" />
              </div>
            )}
          </div>

          {openItems.length < openTotal && (
            <p className="border-t border-gray01 p-3 text-xs text-black04">
              Mostrando {openItems.length} de {openTotal}
            </p>
          )}
        </div>
      )}
    </ResultCard>
  );
};

interface RelatedCardProps {
  title: string;
  count: number;
  main: string;
  subtitle?: string;
  isOpen?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
}

export const RelatedCard = ({
  title,
  count,
  main,
  subtitle,
  isOpen,
  isLoading,
  onClick,
}: RelatedCardProps) => (
  <button
    type="button"
    disabled={!onClick || count === 0}
    onClick={onClick}
    className={cn(
      // min-w-0/items-stretch: o <button> não estica os filhos sozinho, e aí o
      // texto longo vaza em vez de cortar
      "flex min-w-0 flex-col items-stretch gap-1 rounded-lg border border-gray01 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 disabled:cursor-default disabled:hover:bg-gray-50",
      isOpen && "border-primary",
      isLoading && "border-dashed border-gray01",
    )}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-black04">
        {title}
      </span>
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-black04" />
      ) : (
        <span className="rounded-xl bg-gray02 px-2 py-[1.5px] text-xs font-semibold text-black03">
          {count}
        </span>
      )}
    </div>

    <span
      className={cn(
        "truncate font-semibold text-black01",
        isLoading && "text-black04",
      )}
    >
      {isLoading ? "Carregando..." : main}
    </span>
    {/* o span vazio segura a altura do card quando não há subtítulo */}
    <span className="truncate text-xs text-black04">
      {isLoading ? "" : subtitle}
    </span>
  </button>
);

interface TransactionItemProps {
  transaction: Transaction;
  onOpen: () => void;
  productsCatalog?: ProductCatalogItem[];
}

const TransactionItem = ({
  transaction,
  onOpen,
  productsCatalog,
}: TransactionItemProps) => (
  <ResultCard
    title="Abrir transação"
    onOpen={onOpen}
    className="flex flex-col items-start gap-3 md:flex-row md:items-center"
  >
    {!!transaction.type && (
      <PartnerImage
        type={transaction.type as PartnerType}
        products={productsCatalog}
      />
    )}

    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="text-lg font-semibold text-black01">
        #{transaction.contract || "-"}
      </span>
      <span className="text-sm font-medium text-black04">
        Cliente: {formatName(transaction.customerName)} · CPF{" "}
        {formatCpf(transaction.customerCpf)}
      </span>
      <span className="text-sm font-medium text-black04">
        Vendedor: {formatName(transaction.sellerName)} · CPF{" "}
        {formatCpf(transaction.sellerCpf)}
      </span>
    </div>
  </ResultCard>
);
